/**
 * Resync a quotation with its BOQ / Trading-Rental estimation (full page).
 * Preview → per-line decisions → apply. Sent / approved / accepted quotations are resynced on a new revision.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { formatMoney, formatPercent } from '@/utils/format';
import quotationService, {
  type ReconciliationDecision,
  type SourceSyncChange,
} from '@/services/quotation.service';
import { QUOTATION_KEYS, useCreateQuotationRevision } from '@/hooks/useQuotation';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const { Title, Text } = Typography;

type FilterKey = 'needs_decision' | 'all' | 'added' | 'removed' | 'changed' | 'cost';

const CHANGE_LABEL: Record<string, { label: string; color: string }> = {
  ADDED: { label: 'New in estimation', color: 'green' },
  REMOVED: { label: 'Removed from estimation', color: 'red' },
  QUANTITY_CHANGED: { label: 'Quantity changed', color: 'orange' },
  PRICE_CHANGED: { label: 'Price changed', color: 'gold' },
  DESCRIPTION_CHANGED: { label: 'Description changed', color: 'blue' },
  SPECIFICATION_CHANGED: { label: 'Specification changed', color: 'geekblue' },
  COST_CHANGED: { label: 'Cost only', color: 'default' },
};

const DECISION_LABEL: Record<ReconciliationDecision, string> = {
  ACCEPT_ESTIMATE_CHANGE: 'Use estimation',
  KEEP_QUOTATION_VALUE: 'Keep quotation',
  ADD: 'Add line',
  IGNORE: 'Skip',
  REMOVE: 'Remove line',
  REVIEW: 'Decide later',
};

const FIELD_LABEL: Record<string, string> = {
  quantity: 'Qty',
  unit_price: 'Unit price',
  discount_percentage: 'Discount %',
  vat_percentage: 'VAT %',
  rental_rate: 'Rental rate',
  rental_duration: 'Duration',
  item_description: 'Description',
  item_code: 'Item code',
  revision: 'Revision',
  uom: 'UOM',
  line_type: 'Line type',
  rental_period_type: 'Rental period',
  rental_start_date: 'Rental start',
  rental_end_date: 'Rental end',
  loaded_cost_amount: 'Cost',
};

const MONEY_FIELDS = new Set(['unit_price', 'rental_rate', 'loaded_cost_amount']);

function showValue(field: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (MONEY_FIELDS.has(field)) return formatMoney(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (typeof v === 'string' && v.length > 60) return `${v.slice(0, 57)}…`;
  return String(v);
}

function lineTitle(row: SourceSyncChange): string {
  const src = row.source_line?.values;
  const q = row.quotation_line;
  const code = String(q?.item_code || src?.item_code || '').trim();
  const desc = String(q?.item_description || src?.item_description || '').trim();
  const firstLine = desc.split(/\r?\n/).find((l) => l.trim()) || '';
  return [code, firstLine].filter(Boolean).join(' — ') || row.change_id;
}

function decisionOf(row: SourceSyncChange, decisions: Record<string, ReconciliationDecision>) {
  const chosen = decisions[row.change_id];
  return chosen && row.allowed_decisions.includes(chosen) ? chosen : row.default_decision;
}

export function QuotationSourceSyncWorkspace() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTablet = useMediaQuery('(max-width: 1180px)');
  const createRevision = useCreateQuotationRevision();

  const [filterChoice, setFilter] = useState<FilterKey | null>(null);
  const [decisions, setDecisions] = useState<Record<string, ReconciliationDecision>>({});

  const decisionList = useMemo(
    () => Object.entries(decisions).map(([change_id, decision]) => ({ change_id, decision })),
    [decisions]
  );

  const {
    data: preview,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['quotation-source-sync', id, decisionList],
    queryFn: () => quotationService.previewSourceSync(id!, decisionList),
    enabled: !!id,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      quotationService.applySourceSync(id!, {
        previewHash: preview!.preview_hash,
        expectedQuotationUpdatedAt: preview!.quotation_updated_at,
        decisions: decisionList,
      }),
    onSuccess: () => {
      message.success('Quotation resynced with the estimation');
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['quotation-source-sync'] });
      navigate(`/growth/quotations/${id}`);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Resync failed');
      if (e?.response?.status === 409) refetch();
    },
  });

  const changes = useMemo(() => preview?.changes ?? [], [preview]);
  const filter: FilterKey = filterChoice ?? (preview?.unresolved_decisions ? 'needs_decision' : 'all');
  const filteredChanges = useMemo(() => {
    switch (filter) {
      case 'needs_decision':
        return changes.filter((r) => r.requires_decision || decisions[r.change_id]);
      case 'added':
        return changes.filter((r) => r.change_type === 'ADDED');
      case 'removed':
        return changes.filter((r) => r.change_type === 'REMOVED');
      case 'changed':
        return changes.filter((r) => !['ADDED', 'REMOVED', 'COST_CHANGED'].includes(r.change_type));
      case 'cost':
        return changes.filter((r) => r.change_type === 'COST_CHANGED');
      default:
        return changes;
    }
  }, [changes, filter, decisions]);

  const setDecision = (changeId: string, decision: ReconciliationDecision) =>
    setDecisions((prev) => ({ ...prev, [changeId]: decision }));

  const setAll = (pick: (row: SourceSyncChange) => ReconciliationDecision | null) => {
    setDecisions((prev) => {
      const next = { ...prev };
      for (const row of changes) {
        const d = pick(row);
        if (d && row.allowed_decisions.includes(d)) next[row.change_id] = d;
      }
      return next;
    });
  };

  const decisionControl = (row: SourceSyncChange, block = false) =>
    row.allowed_decisions.length > 1 ? (
      <Segmented
        size="small"
        block={block}
        options={row.allowed_decisions.map((d) => ({ label: DECISION_LABEL[d] ?? d, value: d }))}
        value={decisionOf(row, decisions)}
        onChange={(v) => setDecision(row.change_id, v as ReconciliationDecision)}
      />
    ) : (
      <Text type="secondary">{DECISION_LABEL[row.default_decision] ?? row.default_decision}</Text>
    );

  const changeTags = (row: SourceSyncChange) => (
    <Space size={4} wrap>
      <Tag color={CHANGE_LABEL[row.change_type]?.color}>{CHANGE_LABEL[row.change_type]?.label ?? row.change_type}</Tag>
      {row.matched_by === 'code_or_position' ? (
        <Tooltip title="This line had lost its link to the estimation and was paired by item code / position. Applying restores the link.">
          <Tag color="purple">Re-linked</Tag>
        </Tooltip>
      ) : null}
      {row.commercially_modified ? (
        <Tooltip title="This line was edited on the quotation after it came from the estimation.">
          <Tag color="magenta">Edited on quotation</Tag>
        </Tooltip>
      ) : null}
      {row.price_basis === 'markup_kept' ? (
        <Tooltip title="The BOQ line has no selling margin, so the quotation's markup is kept and applied to the new cost.">
          <Tag color="cyan">Markup kept</Tag>
        </Tooltip>
      ) : null}
      {row.price_basis === 'quotation' && row.change_type === 'ADDED' ? (
        <Tooltip title="The BOQ line has no selling margin. It will be added at cost — set the selling price on the quotation.">
          <Tag color="volcano">At cost</Tag>
        </Tooltip>
      ) : null}
    </Space>
  );

  const deltaView = (row: SourceSyncChange) => {
    if (row.change_type === 'ADDED') {
      const v = row.source_line?.values;
      return (
        <Text>
          Qty {showValue('quantity', v?.quantity)} × {showValue('unit_price', v?.unit_price)}
        </Text>
      );
    }
    if (row.change_type === 'REMOVED') {
      const q = row.quotation_line;
      return (
        <Text type="secondary">
          On quotation: Qty {showValue('quantity', q?.quantity)} × {showValue('unit_price', q?.unit_price)}
        </Text>
      );
    }
    return (
      <Space direction="vertical" size={0}>
        {row.field_deltas.map((d) => (
          <Text key={d.field}>
            <Text type="secondary">{FIELD_LABEL[d.field] ?? d.field}: </Text>
            <Text delete>{showValue(d.field, d.quotation)}</Text> → <Text strong>{showValue(d.field, d.source)}</Text>
          </Text>
        ))}
      </Space>
    );
  };

  const columns: ColumnsType<SourceSyncChange> = [
    {
      title: '#',
      key: 'line',
      width: 56,
      render: (_, row) => row.quotation_line?.line_number ?? row.source_line?.line_number ?? '',
    },
    { title: 'Item', key: 'item', render: (_, row) => lineTitle(row) },
    { title: 'Change', key: 'change', width: 230, render: (_, row) => changeTags(row) },
    { title: 'Quotation → Estimation', key: 'delta', render: (_, row) => deltaView(row) },
    {
      title: 'Decision',
      key: 'decision',
      width: 300,
      render: (_, row) => (
        <Space direction="vertical" size={2}>
          {decisionControl(row)}
          {row.requires_decision ? <Text type="warning">Needs your decision</Text> : null}
        </Space>
      ),
    },
  ];

  const onCreateRevision = () => {
    if (!preview) return;
    Modal.confirm({
      title: `Create a new revision of ${preview.quotation_number}?`,
      content: `This quotation can no longer be edited. A new Draft revision will be created and you will review the ${preview.source.number} changes on it. The current revision stays unchanged.`,
      okText: 'Create revision',
      onOk: async () => {
        const res = await createRevision.mutateAsync({
          id: preview.quotation_id,
          revisionReason: `Resync with estimation ${preview.source.number}`,
          revisedData: { revision_type: 'ESTIMATE_CHANGE' },
        });
        const newId = (res as { data?: { id?: string }; id?: string })?.data?.id || (res as { id?: string })?.id;
        if (newId) {
          setDecisions({});
          navigate(`/growth/quotations/${newId}/source-sync`);
        }
      },
    });
  };

  const onApply = () => {
    if (!preview) return;
    const impact = preview.commercial_impact;
    Modal.confirm({
      title: `Resync ${preview.quotation_number} with ${preview.source.number}?`,
      width: 520,
      content: (
        <Space direction="vertical">
          <Text>
            {changes.filter((c) => c.change_type === 'ADDED' && decisionOf(c, decisions) === 'ADD').length} line(s) added ·{' '}
            {changes.filter((c) => c.change_type === 'REMOVED' && decisionOf(c, decisions) === 'REMOVE').length} removed ·{' '}
            {
              changes.filter(
                (c) =>
                  !['ADDED', 'REMOVED', 'COST_CHANGED'].includes(c.change_type) &&
                  decisionOf(c, decisions) === 'ACCEPT_ESTIMATE_CHANGE'
              ).length
            }{' '}
            updated from the estimation
          </Text>
          <Text>
            Selling total: {formatMoney(impact.current_sell)} → <Text strong>{formatMoney(impact.projected_sell)}</Text>
          </Text>
          <Text type="secondary">
            Projected margin: {formatPercent(impact.projected_margin_percent, { nullDisplay: '—' })}
          </Text>
          <Text type="secondary">Costs and estimation links are refreshed on every matched line.</Text>
        </Space>
      ),
      okText: 'Apply resync',
      onOk: () => applyMutation.mutateAsync(),
    });
  };

  const loadError = error as { response?: { data?: { message?: string } }; message?: string } | null;
  const eligibility = preview?.eligibility;
  const sourcePath =
    preview?.source.kind === 'boq'
      ? `/growth/estimations/boq/edit/${preview.source.id}`
      : preview
        ? `/growth/estimations/trading-rental/${preview.source.id}`
        : null;
  const nothingToDo = !!preview && changes.length === 0 && preview.summary.relinked === 0;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }} wrap>
          <Space direction="vertical" size={0}>
            <Title level={4} style={{ margin: 0 }}>
              Resync with estimation
            </Title>
            {preview ? (
              <Text type="secondary">
                {preview.quotation_number} R{preview.revision_number} ({preview.quotation_status}) ←{' '}
                {preview.source.kind === 'boq' ? 'BOQ' : 'Trading / Rental'}{' '}
                <a onClick={() => sourcePath && navigate(sourcePath)}>{preview.source.number}</a> ({preview.source.status})
                {preview.last_synced_at
                  ? ` · last synced ${new Date(preview.last_synced_at).toLocaleString()}`
                  : ''}
              </Text>
            ) : null}
          </Space>
          <Button onClick={() => navigate(`/growth/quotations/${id}`)}>Back to quotation</Button>
        </Space>

        {loadError ? (
          <Alert
            type="error"
            showIcon
            message="Could not compare with the estimation"
            description={loadError.response?.data?.message || loadError.message}
          />
        ) : null}

        {preview?.blocking_issues.map((b) => <Alert key={b} type="error" showIcon message={b} />)}

        {eligibility && !eligibility.can_apply && preview && preview.blocking_issues.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            message={eligibility.reason}
            description={
              eligibility.needs_revision
                ? 'Create a new revision to bring the estimation changes into this quotation. The current revision is kept as-is for audit.'
                : !eligibility.can_revise && eligibility.revise_reason
                  ? eligibility.revise_reason
                  : undefined
            }
            action={
              eligibility.needs_revision && eligibility.can_revise && !nothingToDo ? (
                <Button type="primary" loading={createRevision.isPending} onClick={onCreateRevision}>
                  Create revision &amp; resync
                </Button>
              ) : undefined
            }
          />
        ) : null}

        {preview?.warnings.map((w) => <Alert key={w} type="info" showIcon message={w} />)}

        {nothingToDo ? (
          <Alert
            type="success"
            showIcon
            message="This quotation is in sync with the estimation"
            description={
              preview.summary.manual_lines_kept > 0
                ? `${preview.summary.manual_lines_kept} manually added line(s) are not part of the estimation.`
                : undefined
            }
          />
        ) : null}

        <Card loading={isLoading}>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={4}>
              <Statistic title="New lines" value={preview?.summary.added ?? 0} />
            </Col>
            <Col xs={12} md={4}>
              <Statistic title="Removed lines" value={preview?.summary.removed ?? 0} />
            </Col>
            <Col xs={12} md={4}>
              <Statistic
                title="Changed lines"
                value={
                  (preview?.summary.quantity ?? 0) +
                  (preview?.summary.price ?? 0) +
                  (preview?.summary.description ?? 0) +
                  (preview?.summary.specification ?? 0)
                }
              />
            </Col>
            <Col xs={12} md={4}>
              <Statistic title="Cost only" value={preview?.summary.cost_only ?? 0} />
            </Col>
            <Col xs={12} md={4}>
              <Statistic title="Selling total after" value={formatMoney(preview?.commercial_impact.projected_sell)} />
              <Text type="secondary">now {formatMoney(preview?.commercial_impact.current_sell)}</Text>
            </Col>
            <Col xs={12} md={4}>
              <Statistic
                title="Margin after"
                value={formatPercent(preview?.commercial_impact.projected_margin_percent, { nullDisplay: '—' })}
              />
              <Text type="secondary">
                now {formatPercent(preview?.commercial_impact.current_margin_percent, { nullDisplay: '—' })}
              </Text>
            </Col>
          </Row>
        </Card>

        {!nothingToDo ? (
          <Card
            title={
              <Text>
                {changes.length} change(s) · {preview?.unresolved_decisions ?? 0} need a decision
              </Text>
            }
            extra={
              <Space wrap>
                <Button
                  size="small"
                  onClick={() =>
                    setAll((r) =>
                      r.change_type === 'ADDED'
                        ? 'ADD'
                        : r.change_type === 'REMOVED'
                          ? 'REMOVE'
                          : 'ACCEPT_ESTIMATE_CHANGE'
                    )
                  }
                >
                  Take all from estimation
                </Button>
                <Button
                  size="small"
                  onClick={() => setAll((r) => (r.change_type === 'ADDED' ? 'IGNORE' : 'KEEP_QUOTATION_VALUE'))}
                >
                  Keep all quotation values
                </Button>
                <Segmented
                  value={filter}
                  onChange={(v) => setFilter(v as FilterKey)}
                  options={[
                    { label: 'Needs decision', value: 'needs_decision' },
                    { label: 'All', value: 'all' },
                    { label: 'New', value: 'added' },
                    { label: 'Removed', value: 'removed' },
                    { label: 'Changed', value: 'changed' },
                    { label: 'Cost only', value: 'cost' },
                  ]}
                />
              </Space>
            }
          >
            {filteredChanges.length === 0 ? (
              <Empty description={filter === 'needs_decision' ? 'Nothing needs a decision' : 'No changes in this view'} />
            ) : isTablet ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                {filteredChanges.map((row) => (
                  <Card key={row.change_id} size="small" title={lineTitle(row)} extra={changeTags(row)}>
                    {deltaView(row)}
                    <div style={{ marginTop: 8 }}>{decisionControl(row, true)}</div>
                  </Card>
                ))}
              </Space>
            ) : (
              <Table
                rowKey="change_id"
                size="small"
                dataSource={filteredChanges}
                columns={columns}
                pagination={{ pageSize: 25, hideOnSinglePage: true }}
              />
            )}
          </Card>
        ) : null}

        <Space>
          <Button loading={isFetching && !isLoading} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button
            type="primary"
            disabled={
              !preview ||
              !eligibility?.can_apply ||
              nothingToDo ||
              (preview?.unresolved_decisions ?? 0) > 0 ||
              isFetching ||
              applyMutation.isPending
            }
            loading={applyMutation.isPending}
            onClick={onApply}
          >
            Apply resync
          </Button>
          {(preview?.unresolved_decisions ?? 0) > 0 ? (
            <Text type="warning">Decide the highlighted lines before applying.</Text>
          ) : null}
        </Space>
      </Space>
    </div>
  );
}
